import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";

const HospitalBedModal = ({ isOpen, onClose, onSave, bedToEdit }) => {
  const isEditMode = !!bedToEdit;
  const [bedData, setBedData] = useState({
    bedNo: isEditMode ? bedToEdit?.bedNo || "" : "",
    wcCode: isEditMode ? bedToEdit?.wcCode || "" : "",
    wcDetail: isEditMode ? bedToEdit?.wcDetail || "" : "",
    rent: isEditMode ? String(bedToEdit?.rent || "") : "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setBedData({
        bedNo: isEditMode ? bedToEdit?.bedNo || "" : "",
        wcCode: isEditMode ? bedToEdit?.wcCode || "" : "",
        wcDetail: isEditMode ? bedToEdit?.wcDetail || "" : "",
        rent: isEditMode ? String(bedToEdit?.rent || "") : "", 
      });
      setErrors({});
    }
  }, [isOpen, bedToEdit]);

  const validateForm = () => {
    const newErrors = {};
    let hasError = false;

    // Strict validation for add mode
  if (!isEditMode) {
      if (!bedData.bedNo.trim()) {
        newErrors.bedNo = "Bed Number is required";
        hasError = true;
      }
      if (!bedData.rent) {
        newErrors.rent = "Rent is required";
        hasError = true;
      } else if (isNaN(bedData.rent) || Number(bedData.rent) <= 0) {
        newErrors.rent = "Rent must be a positive number";
        hasError = true;
      }
    }

    // In edit mode, ensure at least one field is provided (PUT requirement)
    if (isEditMode) {
      const hasChanges =
        bedData.bedNo.trim() ||
        bedData.wcCode.trim() ||
        bedData.wcDetail.trim() ||
        bedData.rent;
        
      if (!hasChanges) {
        newErrors.form = "At least one field must be provided to update";
        hasError = true;
      } else if (bedData.rent && (isNaN(bedData.rent) || Number(bedData.rent) <= 0)) {
        newErrors.rent = "Rent must be a positive number";
        hasError = true;
      }
    }

    setErrors(newErrors);
    if (hasError) {
      toast.error(newErrors.form || "Please fix the errors in the form", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return !hasError;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBedData((prevData) => ({ ...prevData, [name]: value }));
    if (value || (!isEditMode && name !== "rent")) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        delete newErrors.form;
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let savedBed;
      if (isEditMode) {
        // For edit mode, call PUT API with only provided fields
        const payload = {};
        if (bedData.bedNo.trim()) payload.bedNumber = bedData.bedNo.trim();
        if (bedData.wcCode.trim()) payload.wcCode = bedData.wcCode.trim();
        if (bedData.wcDetail.trim()) payload.wcDetail = bedData.wcDetail.trim();
        if (bedData.rent) payload.rent = parseFloat(bedData.rent);

        // console.log("PUT request payload:", payload); // Debug payload
        const response = await axios.put(
          `https://api.muktihospital.com/api/hospital/bed/edit/${bedToEdit.id}`,
          payload,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        // console.log("PUT response:", response.data); // Debug response
        savedBed = {
          id: bedToEdit.id,
          bedNo: response.data.bed.bedNumber || bedData.bedNo.trim() || "",
          wcCode: response.data.bed.wcCode || bedData.wcCode.trim() || "",
          wcDetail: response.data.bed.wcDetail || bedData.wcDetail.trim() || "",
          rent: String(response.data.bed.rent || bedData.rent || ""), // Convert rent to string
        };
        // console.log("Saved bed:", savedBed); // Debug savedBed
        toast.success("Bed updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        // For add mode, call POST API
        const payload = {
          bedNumber: bedData.bedNo.trim(),
          wcCode: bedData.wcCode.trim(),
          wcDetail: bedData.wcDetail.trim() || "",
          rent: parseFloat(bedData.rent),
        };
        // console.log("POST request payload:", payload); // Debug payload
        const response = await axios.post(
          "https://api.muktihospital.com/api/hospital/bed/add",
          payload,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        // console.log("POST response:", response.data); // Debug response
        savedBed = {
          id: response.data.bed.id || Math.random().toString(36).substr(2, 9),
          bedNo: response.data.bed.bedNumber || bedData.bedNo.trim(),
          wcCode: response.data.bed.wcCode || bedData.wcCode.trim(),
          wcDetail: response.data.bed.wcDetail || bedData.wcDetail.trim() || "",
          rent: String(response.data.bed.rent || bedData.rent),
        };
        // console.log("Saved bed:", savedBed); // Debug savedBed
        toast.success("Bed added successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
      onSave(savedBed, isEditMode);
      onClose();
    } catch (error) {
      console.error("Error saving bed:", error);
      if (error.response?.status === 409) {
        toast.error("A bed with this number already exists. Please choose a unique number.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.error || "Missing or invalid bed fields.", {
          position: "top-right",
          autoClose: 3000,
        });
      } else if (error.response?.status === 404) {
        toast.error("Bed not found.", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("Failed to save bed.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[100vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">{isEditMode ? "Edit Bed" : "Add Bed"}</h2>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bed Number <span className="text-red-500">{!isEditMode ? "*" : ""}</span>
            </label>
            <input
              type="text"
              name="bedNo"
              value={bedData.bedNo}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.bedNo ? "border-red-500" : ""
              }`}
              placeholder="Enter bed number"
              disabled={isLoading}
            />
            {errors.bedNo && (
              <span className="text-red-500 text-xs mt-1">{errors.bedNo}</span>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              W/C Code
            </label>
            <input
              type="text"
              name="wcCode"
              value={bedData.wcCode}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter W/C code"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              W/C Detail
            </label>
            <input
              type="text"
              name="wcDetail"
              value={bedData.wcDetail}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter W/C detail"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rent <span className="text-red-500">{!isEditMode ? "*" : ""}</span>
            </label>
            <input
              type="text"
              name="rent"
              value={bedData.rent}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.rent ? "border-red-500" : ""
              }`}
              placeholder="Enter rent"
              disabled={isLoading}
            />
            {errors.rent && (
              <span className="text-red-500 text-xs mt-1">{errors.rent}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HospitalBedModal;